import React from 'react';
import { TI_CHANNELS, TI_STATUSES, TI_STATUS_KEYS } from '../../triage/config';
import { getTriageCurrentUserId, getTriageStaff } from '../../triage/identity';
import {
  tiAssigneeLabel,
  tiCurrentUser,
  tiStatusLabel,
  tiToggleSelection,
} from '../../triage/utils';
import type {
  TriageAssigneeSelection,
  TriageChannelFilter,
  TriageFilterCounts,
  TriageItem,
  TriageSegment,
  TriageStatus,
  TriageStatusSelection,
} from '../../triage/types';
import { Button } from '../Button/Button';
import { EmptyState } from '../EmptyState/EmptyState';
import { Icon } from '../Icon/Icon';
import { TriageAssigneeAvatar } from './TriageAssigneeAvatar';
import { TriageFilterButton, TriageFilterCheckRow, TriageFilterDropdown } from './TriageFilterDropdown';
import { TriageListRow } from './TriageListRow';

export interface TriageListPanelProps {
  items: TriageItem[];
  segment: TriageSegment;
  onSegmentChange: (segment: TriageSegment) => void;
  attentionCount: number;
  counts: TriageFilterCounts;
  statusSelection: TriageStatusSelection;
  onStatusSelectionChange: (selection: TriageStatusSelection) => void;
  channelFilter: TriageChannelFilter;
  onChannelFilterChange: (filter: TriageChannelFilter) => void;
  assigneeSelection: TriageAssigneeSelection;
  onAssigneeSelectionChange: (selection: TriageAssigneeSelection) => void;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  selectedId: number | null;
  onSelectId: (id: number) => void;
  /** Overrides the default empty box shown when `items` is empty. */
  emptyState?: React.ReactNode;
  /** When provided, the "no match" empty box offers a reset action. */
  onClearFilters?: () => void;
}

export function TriageListPanel({
  items,
  segment,
  onSegmentChange,
  attentionCount,
  counts,
  statusSelection,
  onStatusSelectionChange,
  channelFilter,
  onChannelFilterChange,
  assigneeSelection,
  onAssigneeSelectionChange,
  searchQuery,
  onSearchQueryChange,
  selectedId,
  onSelectId,
  emptyState,
  onClearFilters,
}: TriageListPanelProps) {
  const me = tiCurrentUser();
  const statusTotal = Object.values(counts.status).reduce((s, n) => s + (n ?? 0), 0);

  return (
    <div
      className="flex min-h-0 shrink-0 flex-col"
      style={{
        width: 380,
        background: 'var(--cream-1)',
        borderRight: '1px solid var(--cream-2)',
      }}
    >
      <div style={{ padding: '13px 16px 12px', borderBottom: '1px solid var(--cream-2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9 }}>
          <div
            style={{
              display: 'inline-flex',
              gap: 3,
              padding: 3,
              background: 'var(--surface-mute)',
              borderRadius: 9,
            }}
          >
            {(
              // Spec 31 §7: the "Calls to make" tab is gone — anything whose next
              // step is a phone call lives in the Calls section now. Only Attention
              // remains here (Waiting / Done are the status axis, not a segment).
              [
                { key: 'attention' as const, label: 'Attention', count: attentionCount },
              ] as const
            ).map((seg) => {
              const isActive = segment === seg.key;
              return (
                <button
                  key={seg.key}
                  type="button"
                  onClick={() => onSegmentChange(seg.key)}
                  style={{
                    padding: '5px 11px',
                    borderRadius: 7,
                    fontSize: 12,
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? 'var(--ink-0)' : 'var(--ink-2)',
                    background: isActive ? 'var(--cream-1)' : 'transparent',
                    border: isActive ? '1px solid var(--cream-2)' : '1px solid transparent',
                    boxShadow: isActive ? 'var(--sh-1)' : 'none',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-body)',
                    letterSpacing: '-0.005em',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {seg.label}
                  <span className="v6-mono" style={{ fontSize: 10, fontWeight: 700, color: isActive ? 'var(--ink-2)' : 'var(--ink-3)' }}>
                    {seg.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 9, flexWrap: 'wrap' }}>
          {segment === 'attention' ? (
            <TriageFilterDropdown
              minWidth={222}
              trigger={({ open, toggle }) => {
                const cfg = channelFilter !== 'All' ? TI_CHANNELS[channelFilter] : null;
                return (
                  <TriageFilterButton
                    open={open}
                    toggle={toggle}
                    active={!!cfg}
                    lead={
                      cfg ? (
                        <Icon kind={cfg.icon} size={12} stroke={2} color={cfg.color} className="shrink-0" />
                      ) : (
                        <Icon kind="list" size={12} stroke={2} color="var(--ink-3)" className="shrink-0" />
                      )
                    }
                    label={cfg ? cfg.shortLabel : 'Channels'}
                    count={cfg ? (counts.channel[channelFilter] ?? 0) : null}
                  />
                );
              }}
            >
              {({ close }) => (
                <>
                  <div className="ti-menu-eyebrow">Channel</div>
                  <TriageFilterCheckRow
                    multi={false}
                    checked={channelFilter === 'All'}
                    label="All channels"
                    onClick={() => {
                      onChannelFilterChange('All');
                      close();
                    }}
                  />
                  {Object.entries(TI_CHANNELS).map(([key, cfg]) => (
                    <TriageFilterCheckRow
                      key={key}
                      multi={false}
                      checked={channelFilter === key}
                      lead={<Icon kind={cfg.icon} size={13} stroke={2} color={cfg.color} className="shrink-0" />}
                      label={cfg.label}
                      count={counts.channel[key] ?? 0}
                      onClick={() => {
                        onChannelFilterChange(key as TriageChannelFilter);
                        close();
                      }}
                    />
                  ))}
                </>
              )}
            </TriageFilterDropdown>
          ) : null}

          <TriageFilterDropdown
            minWidth={246}
            trigger={({ open, toggle }) => {
              const single = assigneeSelection.length === 1 ? assigneeSelection[0] : null;
              const leadAv =
                single === 'mine' ? (
                  <TriageAssigneeAvatar staff={me} size={15} />
                ) : single && single !== 'unassigned' ? (
                  <TriageAssigneeAvatar staff={getTriageStaff().find((s) => s.id === single) ?? null} size={15} />
                ) : null;
              return (
                <TriageFilterButton
                  open={open}
                  toggle={toggle}
                  active={assigneeSelection.length > 0}
                  lead={leadAv}
                  label={tiAssigneeLabel(assigneeSelection)}
                  count={assigneeSelection.length > 1 ? assigneeSelection.length : null}
                />
              );
            }}
          >
            {() => (
              <>
                <div className="ti-menu-eyebrow">Assignee</div>
                <TriageFilterCheckRow
                  multi={false}
                  checked={assigneeSelection.length === 0}
                  label="Anyone"
                  onClick={() => onAssigneeSelectionChange([])}
                />
                <TriageFilterCheckRow
                  checked={assigneeSelection.includes('mine')}
                  lead={<TriageAssigneeAvatar staff={me} size={20} />}
                  label={`Me · ${me.name.split(' ')[0]}`}
                  sub={me.role}
                  count={counts.mine}
                  onClick={() => onAssigneeSelectionChange(tiToggleSelection(assigneeSelection, 'mine'))}
                />
                <TriageFilterCheckRow
                  checked={assigneeSelection.includes('unassigned')}
                  lead={
                    <span
                      className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-dashed border-[var(--cream-shadow)] text-[var(--ink-3)]"
                    >
                      <Icon kind="team" size={11} stroke={2} />
                    </span>
                  }
                  label="Unassigned"
                  count={counts.unassigned}
                  onClick={() => onAssigneeSelectionChange(tiToggleSelection(assigneeSelection, 'unassigned'))}
                />
                <div className="ti-menu-item-divider" />
                <div className="ti-menu-eyebrow">By staff</div>
                {getTriageStaff().filter((s) => s.id !== getTriageCurrentUserId()).map((s) => (
                  <TriageFilterCheckRow
                    key={s.id}
                    checked={assigneeSelection.includes(s.id)}
                    lead={<TriageAssigneeAvatar staff={s} size={20} />}
                    label={s.name}
                    sub={s.role}
                    onClick={() => onAssigneeSelectionChange(tiToggleSelection(assigneeSelection, s.id))}
                  />
                ))}
              </>
            )}
          </TriageFilterDropdown>

          <TriageFilterDropdown
            minWidth={210}
            align="right"
            trigger={({ open, toggle }) => (
              <TriageFilterButton
                open={open}
                toggle={toggle}
                active={statusSelection.length > 0}
                lead={
                  statusSelection.length === 1 ? (
                    <span
                      className="ti-status-dot h-[7px] w-[7px] shrink-0"
                      style={{ background: TI_STATUSES[statusSelection[0]!]?.dot }}
                    />
                  ) : null
                }
                label={tiStatusLabel(statusSelection)}
                count={statusSelection.length > 1 ? statusSelection.length : null}
              />
            )}
          >
            {() => (
              <>
                <div className="ti-menu-eyebrow">Status</div>
                <TriageFilterCheckRow
                  multi={false}
                  checked={statusSelection.length === 0}
                  label="All statuses"
                  count={statusTotal}
                  onClick={() => onStatusSelectionChange([])}
                />
                {TI_STATUS_KEYS.map((key) => {
                  const statusDot = TI_STATUSES[key]?.dot;
                  return (
                    <TriageFilterCheckRow
                      key={key}
                      checked={statusSelection.includes(key)}
                      {...(statusDot ? { dot: statusDot } : {})}
                      label={TI_STATUSES[key]?.label ?? key}
                      count={counts.status[key as TriageStatus] ?? 0}
                      onClick={() => onStatusSelectionChange(tiToggleSelection(statusSelection, key))}
                    />
                  );
                })}
              </>
            )}
          </TriageFilterDropdown>
        </div>

        <div style={{ position: 'relative' }}>
          <Icon
            kind="search"
            size={13}
            stroke={2}
            color="var(--ink-3)"
            style={{
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
            }}
          />
          <input
            value={searchQuery}
            onChange={(e) => onSearchQueryChange(e.target.value)}
            placeholder="Search patient, subject, tag…"
            style={{
              width: '100%',
              height: 32,
              padding: '0 28px 0 30px',
              border: '1px solid var(--cream-2)',
              borderRadius: 8,
              fontSize: 12.5,
              color: 'var(--ink-0)',
              outline: 'none',
              boxSizing: 'border-box',
              fontFamily: 'var(--font-body)',
              letterSpacing: '-0.005em',
              background: 'var(--cream-1)',
              boxShadow: 'var(--sh-1)',
            }}
          />
          {searchQuery ? (
            <button
              type="button"
              onClick={() => onSearchQueryChange('')}
              style={{
                position: 'absolute',
                right: 7,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 4,
                borderRadius: 4,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--ink-3)',
              }}
            >
              <Icon kind="x" size={11} stroke={2} />
            </button>
          ) : null}
        </div>
      </div>

      <div className="cal-scroll flex min-h-0 flex-1 flex-col gap-[5px] overflow-y-auto" style={{ padding: '10px 12px' }}>
        {items.map((item, i) => (
          <TriageListRow
            key={item.id}
            item={item}
            isSelected={selectedId === item.id}
            onSelect={() => onSelectId(item.id)}
            index={i}
          />
        ))}
        {items.length === 0
          ? (emptyState ??
            (searchQuery ? (
              <EmptyState
                size="sm"
                variant="box"
                fill
                icon="search"
                title={`No results for “${searchQuery}”`}
                action={
                  <Button size="sm" variant="secondary" onClick={() => onSearchQueryChange('')}>
                    Clear search
                  </Button>
                }
              />
            ) : (
              <EmptyState
                size="sm"
                variant="box"
                fill
                icon="filter"
                title="No items match this filter."
                action={
                  onClearFilters ? (
                    <Button size="sm" variant="secondary" onClick={onClearFilters}>
                      Reset filters
                    </Button>
                  ) : undefined
                }
              />
            )))
          : null}
      </div>
    </div>
  );
}
